import { useQuery } from "@tanstack/react-query";

import Modal, { Drawer } from "@/components/ui/Modal.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { Callout, EmptyState } from "@/components/ui/Display.jsx";
import { TableWrap, Td, Th, Tr } from "@/components/ui/Table.jsx";
import { usageService } from "@/lib/api.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { formatDate, formatDuration, formatNumber, formatTime } from "@/lib/format.js";
import { PRODUCTIVITY, toneOf } from "@/lib/tokens.js";
import { cx } from "@/lib/cx.js";

/*
 * The three overlays the markup opens from this page: an app-sessions modal and
 * two detail drawers.
 *
 * Both drawers fetch on open rather than being handed pre-computed numbers. The
 * markup faked a time entry's app breakdown by scaling the DAY's usage by the
 * entry's share of the day — which produces a plausible-looking list where
 * every entry has the same apps in the same proportions. The real breakdown
 * comes from /api/usage/time-entry/:id, and an entry where someone only used
 * one app should say so.
 */

/* ---- Key/value row shared by both drawers ------------------------------- */
function Fact({ label, children }) {
    return (
        <div className="flex items-baseline gap-3 border-b border-slate-100 py-2.5 last:border-0 dark:border-slate-800">
            <span className="w-32 shrink-0 text-[11px] font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
                {label}
            </span>
            <span className="min-w-0 flex-1 text-[13px] font-medium break-words">{children}</span>
        </div>
    );
}

function Counter({ value, label, className }) {
    return (
        <div className="bg-white p-3 text-center dark:bg-slate-900">
            <p className={cx("font-mono text-base font-semibold", className)}>{value}</p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500">{label}</p>
        </div>
    );
}

/* ---- Time entry drawer -------------------------------------------------- */

export function TimeEntryDrawer({ entry, onClose }) {
    const detail = useQuery({
        queryKey: ["time-entry-usage", entry.id],
        queryFn: () => usageService.getTimeEntryUsage(entry.id).then((r) => r.data?.data ?? {}),
    });

    const applications = detail.data?.applications ?? [];
    const websites = detail.data?.websites ?? [];
    const inputStats = detail.data?.inputStats ?? {};
    const shotCount = detail.data?.screenshotCount;

    return (
        <Drawer
            open
            onClose={onClose}
            title="Time Entry Detail"
            subtitle={`${formatTime(entry.startTime)} – ${entry.ongoing ? "Ongoing" : formatTime(entry.endTime)} · ${formatDuration(entry.duration)}`}
        >
            <div className="mb-4 grid grid-cols-3 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 dark:border-slate-800 dark:bg-slate-800">
                <Counter value={formatDuration(entry.duration)} label="duration" />
                <Counter
                    value={entry.score == null ? "—" : `${Math.round(entry.score)}%`}
                    label="productivity"
                    className={
                        entry.score == null
                            ? undefined
                            : entry.score >= 70
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-amber-600 dark:text-amber-400"
                    }
                />
                {/* Keystrokes rather than a screenshot count: the entry
                    response carries input totals, and inventing "duration / 5
                    minutes" screenshots would be arithmetic dressed as data. */}
                <Counter value={formatNumber(inputStats.keystrokes ?? 0)} label="keystrokes" />
            </div>

            <div className="mb-5">
                <Fact label="Date">{formatDate(entry.startTime)}</Fact>
                <Fact label="Start Time">
                    <span className="font-mono">{formatTime(entry.startTime)}</span>
                </Fact>
                <Fact label="End Time">
                    {entry.ongoing ? (
                        <Tag tone="brand">Ongoing</Tag>
                    ) : (
                        <span className="font-mono">{formatTime(entry.endTime)}</span>
                    )}
                </Fact>
                <Fact label="Project">{entry.project ?? "—"}</Fact>
                {entry.note && <Fact label="Note">{entry.note}</Fact>}
                {entry.isManual && (
                    <Fact label="Source">
                        <Tag tone="amber">Added manually</Tag>
                    </Fact>
                )}
                <Fact label="Entry ID">
                    <span className="font-mono text-[12px] text-slate-400">{entry.id}</span>
                </Fact>
            </div>

            <p className="mb-2 text-[11px] font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
                App usage in this entry
            </p>

            {detail.isError ? (
                <Callout tone="danger">
                    {apiErrorMessage(detail.error, "Could not load the breakdown for this entry.")}
                </Callout>
            ) : detail.isLoading ? (
                <p className="text-[12.5px] text-slate-400 dark:text-slate-500">Loading…</p>
            ) : (
                <UsageBreakdown items={[...applications, ...websites]} />
            )}

            {shotCount != null && (
                <p className="mt-4 text-[11.5px] text-slate-400 dark:text-slate-500">
                    {shotCount} screenshot{shotCount === 1 ? "" : "s"} captured during this entry.
                </p>
            )}
        </Drawer>
    );
}

function UsageBreakdown({ items }) {
    const rows = items
        .map((i) => ({
            name: i.name || i.domain || i.url || "Unknown",
            ms: i.totalTime ?? i.duration ?? 0,
            productivityStatus: i.productivityStatus || "unrated",
        }))
        .filter((r) => r.ms > 0)
        .sort((a, b) => b.ms - a.ms);

    if (!rows.length) {
        return (
            <p className="text-[12.5px] text-slate-400 dark:text-slate-500">
                No application or website usage was recorded inside this entry.
            </p>
        );
    }

    const total = rows.reduce((sum, r) => sum + r.ms, 0);

    return (
        <div className="space-y-2.5">
            {rows.map((r) => {
                const p = PRODUCTIVITY[r.productivityStatus] ?? PRODUCTIVITY.unrated;
                const tone = toneOf(p.tone);

                return (
                    <div key={r.name} data-entry-app={r.name}>
                        <div className="mb-1 flex items-center gap-2">
                            <span className="truncate text-[12.5px] font-medium">{r.name}</span>
                            <span className={cx("tag shrink-0", tone.tag)}>{p.label}</span>
                            <span className="ml-auto font-mono text-[12px] font-semibold">
                                {formatDuration(r.ms)}
                            </span>
                        </div>
                        <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                            <span
                                className={cx("h-full rounded-full", tone.bar)}
                                style={{ width: `${total ? (r.ms / total) * 100 : 0}%` }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/* ---- Idle drawer -------------------------------------------------------- */

export function IdleTimeDrawer({ idle, entryLabel, onClose }) {
    return (
        <Drawer
            open
            onClose={onClose}
            title="Idle Time Detail"
            subtitle={`${formatTime(idle.startTime)} – ${idle.ongoing ? "still open" : formatTime(idle.endTime)}`}
        >
            <div className="mb-4 rounded-lg border border-rose-100 bg-rose-50/60 p-3.5 dark:border-rose-950 dark:bg-rose-950/30">
                <p className="font-mono text-xl font-semibold text-rose-600 dark:text-rose-400">
                    {idle.ongoing ? "—" : formatDuration(idle.duration)}
                </p>
                <p className="mt-0.5 text-[12px] text-slate-500 dark:text-slate-400">
                    no keyboard or mouse input in this window
                </p>
            </div>

            <div className="mb-4">
                <Fact label="Date">{formatDate(idle.startTime)}</Fact>
                <Fact label="Start Time">
                    <span className="font-mono">{formatTime(idle.startTime)}</span>
                </Fact>
                <Fact label="End Time">
                    {idle.ongoing ? (
                        <Tag tone="amber">Still open</Tag>
                    ) : (
                        <span className="font-mono">{formatTime(idle.endTime)}</span>
                    )}
                </Fact>
                <Fact label="Duration">
                    <span className="font-mono">
                        {idle.ongoing ? "—" : formatDuration(idle.duration)}
                    </span>
                </Fact>
                <Fact label="Session">{entryLabel ?? "—"}</Fact>
                <Fact label="Idle ID">
                    <span className="font-mono text-[12px] text-slate-400">{idle.id}</span>
                </Fact>
            </div>

            <p className="text-[12.5px] text-slate-500 dark:text-slate-400">
                {idle.ongoing
                    ? "This period has no end time yet — either the person is still away, or the desktop app stopped before it could close the record."
                    : "Counted toward the day's idle total. Work time is never credited during an idle period."}
            </p>
        </Drawer>
    );
}

/* ---- App sessions modal ------------------------------------------------- */

/*
 * Every session of one app or site, from /api/usage/app-sessions/:userId.
 *
 * The `type` the endpoint wants is the same one the usage record carries —
 * "application" for an app row, "website"/"domain" for a site — so it is passed
 * straight through rather than guessed from the name.
 */
export function AppSessionsModal({ userId, target, range, onClose }) {
    const sessions = useQuery({
        queryKey: ["app-sessions", userId, target.name, target.type, range.startDate, range.endDate],
        queryFn: () =>
            usageService
                .getAppSessions(userId, {
                    name: target.name,
                    type: target.type,
                    startDate: range.startDate,
                    endDate: range.endDate,
                })
                .then((r) => r.data?.data ?? {}),
    });

    const rows = sessions.data?.sessions ?? [];
    const total = sessions.data?.totalDuration ?? 0;

    return (
        <Modal
            open
            onClose={onClose}
            size="2xl"
            title={target.name}
            subtitle={
                sessions.isLoading
                    ? "Loading sessions…"
                    : `${rows.length} session${rows.length === 1 ? "" : "s"} · ${formatDuration(total)} total`
            }
            bodyClassName="min-h-0 flex-1 overflow-y-auto"
        >
            {sessions.isError ? (
                <div className="p-4.5">
                    <Callout tone="danger">
                        {apiErrorMessage(sessions.error, "Could not load sessions.")}
                    </Callout>
                </div>
            ) : rows.length === 0 && !sessions.isLoading ? (
                <div className="p-4.5">
                    <EmptyState
                        title="No individual sessions recorded"
                        hint="Usage was totalled for this range, but the per-session activity records are not available for it."
                    />
                </div>
            ) : (
                <TableWrap>
                    <thead>
                        <tr>
                            <Th className="pl-4.5">Start</Th>
                            <Th>End</Th>
                            <Th>Duration</Th>
                            <Th className="pr-4.5">Window title</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((s, i) => (
                            <Tr key={`${s.startTime}-${i}`} data-session={i}>
                                <Td className="pl-4.5 font-mono text-[12.5px]">
                                    {formatTime(s.startTime)}
                                </Td>
                                <Td className="font-mono text-[12.5px]">{formatTime(s.endTime)}</Td>
                                <Td>
                                    <span className="font-mono text-[12.5px] font-semibold">
                                        {formatDuration(s.duration || 0)}
                                    </span>
                                </Td>
                                <Td className="pr-4.5 text-slate-500 dark:text-slate-400">
                                    {s.title || "—"}
                                </Td>
                            </Tr>
                        ))}
                    </tbody>
                </TableWrap>
            )}
        </Modal>
    );
}
